import { useI18nStore } from '../../lib/i18n';
import { useEffect, useState, useRef } from 'react';
import { api } from '../../lib/auth';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Calendar, MapPin, Truck, CheckCircle2, AlertCircle, X, Camera, Loader2, ChevronRight, Paperclip, Upload, FileText, Image as ImageIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import StartStopPanel from './start-stop/StartStopPanel';

/**
 * Bugünkü Atama Kartı (Faz 2 — Şub 03, 2026)
 * --------------------------------------------------------------
 * Çalışanın dashboard'unun EN ÜSTÜNDE gösterilen, çok belirgin bir
 * bilgi/CTA kartı. Bugün veya yarın için bir planlama atması varsa:
 *  - Rota, depo, şirket bilgisi
 *  - "Form Doldur" butonu (depo şablonunda alan varsa)
 *  - Form gönderildiyse yeşil onay rozeti
 *
 * Backend: GET /api/planning/my-todays-assignment
 *          POST /api/planning/assignments/{id}/form-submission
 */
export default function TodayAssignmentCard() {
  const [assignments, setAssignments] = useState(null); // [{...}, ...] | null = loading | [] = no data
  const [openFormId, setOpenFormId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/planning/my-todays-assignment');
      setAssignments((res.data?.data || []).filter((a) => a.is_today));
    } catch {
      setAssignments([]);
    }
  };

  useEffect(() => { load(); }, []);

  if (assignments === null) {
    return (
      <div className="mb-4">
        <Card className="p-4 border-dashed">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Atama bilgileri yükleniyor…
          </div>
        </Card>
      </div>
    );
  }
  if (assignments.length === 0) return null;

  return (
    <div className="mb-4 space-y-3" data-testid="today-assignment-section">
      {assignments.map((a) => (
        <div key={a.id} className="space-y-2">
          <AssignmentBanner a={a} onFormOpen={() => setOpenFormId(a.id)} />
          {a.start_stop?.enabled && <StartStopPanel a={a} onChanged={load} />}
        </div>
      ))}
      {assignments.map((a) => (openFormId === a.id ? (
        <AssignmentFormDialog
          key={`f-${a.id}`}
          a={a}
          onClose={() => setOpenFormId(null)}
          onSubmitted={() => { setOpenFormId(null); load(); }}
        />
      ) : null))}
    </div>
  );
}

function AssignmentBanner({ a, onFormOpen }) {
  const isToday = a.is_today;
  const accent = isToday
    ? 'from-blue-500 via-indigo-500 to-violet-500'
    : 'from-amber-400 via-orange-400 to-amber-500';
  const labelDot = isToday ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';
  const hasForm = (a.form_template || []).length > 0;
  const submitted = !!a.form_submitted;
  const ss = a.start_stop || {};
  const formLockedBySS = ss.required && a.start_stop_record?.status !== 'stopped';

  return (
    <Card
      className="relative overflow-hidden border-0 shadow-lg"
      data-testid={`today-assignment-card-${a.id}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-r ${accent} opacity-95`} />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2040%2040%22%3E%3Ccircle%20cx%3D%2220%22%20cy%3D%2220%22%20r%3D%221%22%20fill%3D%22%23fff%22%20fill-opacity%3D%220.07%22%2F%3E%3C%2Fsvg%3E')]" />
      <div className="relative p-5 text-white flex items-center gap-4">
        <div className="flex-shrink-0">
          <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${labelDot}`}>
            {a.is_overnight_carry ? useI18nStore.getState().t('startStop.overnightBadge') : useI18nStore.getState().t(isToday ? 'startStop.todayBadge' : 'startStop.tomorrowBadge')}
          </span>
          <p className="text-3xl font-bold mt-1 leading-none">
            {new Date(a.assignment_date).toLocaleDateString('tr-TR', { day: '2-digit' })}
          </p>
          <p className="text-[11px] opacity-90 mt-0.5">
            {new Date(a.assignment_date).toLocaleDateString('tr-TR', { month: 'short', weekday: 'short' })}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider opacity-80">Rota / Tur</p>
          <p className="text-xl font-bold truncate">
            {a.route_code} {a.route_name && <span className="font-normal opacity-90">· {a.route_name}</span>}
          </p>
          <div className="flex items-center gap-3 text-xs opacity-90 mt-1.5 flex-wrap">
            {a.company_name && (
              <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> {a.company_name}</span>
            )}
            {a.depot_name && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.depot_name}</span>
            )}
            {a.start_time && (
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {a.start_time}</span>
            )}
            {a.vehicle_code && (
              <span className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded">{a.vehicle_code}</span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
          {!hasForm ? (
            <Badge className="bg-white/20 text-white border-0 text-[10px]">{ss.enabled ? 'Start-Stop' : 'Form yok'}</Badge>
          ) : submitted ? (
            <Badge className="bg-emerald-500/90 text-white border-0 gap-1" data-testid={`today-form-submitted-${a.id}`}>
              <CheckCircle2 className="h-3 w-3" /> Form dolduruldu
            </Badge>
          ) : !isToday ? (
            // Yarın atamaları için form kilitli — sadece bugün doldurulabilir
            <Badge className="bg-white/20 text-white border-0 gap-1" data-testid={`today-form-locked-${a.id}`}>
              <AlertCircle className="h-3 w-3" /> Yarın açılacak
            </Badge>
          ) : formLockedBySS ? (
            <Badge className="bg-white/20 text-white border-0 gap-1" data-testid={`today-form-ss-locked-${a.id}`}>
              <AlertCircle className="h-3 w-3" /> {useI18nStore.getState().t('startStop.afterStop')}
            </Badge>
          ) : (
            <>
              <Badge className="bg-rose-500/90 text-white border-0 gap-1 animate-pulse" data-testid={`today-form-pending-${a.id}`}>
                <AlertCircle className="h-3 w-3" /> Form bekliyor
              </Badge>
              <Button
                size="sm"
                onClick={onFormOpen}
                className="bg-white text-slate-900 hover:bg-slate-100 gap-1 font-semibold"
                data-testid={`today-form-open-${a.id}`}
              >
                Doldur <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function AssignmentFormDialog({ a, onClose, onSubmitted }) {
  const initialAnswers = a.form_submission?.answers || {};
  const initialPhotos = a.form_submission?.photos || {};

  // Şub 09, 2026: time/date varsayılanı artık şablondaki `default_now` flag'ine bağlı.
  //   - Bitiş saati için flag açıkken → şimdiki saatle dolu gelir
  //   - Başlangıç saati için flag kapalıyken → boş kalır (kullanıcı kendi girer)
  const seededAnswers = (() => {
    if (a.form_submission) return initialAnswers;  // mevcut submission ise dokunma
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const yyyy = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const today = `${yyyy}-${MM}-${dd}`;
    const seed = { ...initialAnswers };
    for (const f of (a.form_template || [])) {
      if (seed[f.key] != null && seed[f.key] !== '') continue;
      if (!f.default_now) continue;
      if (f.type === 'time') seed[f.key] = `${hh}:${mm}`;
      else if (f.type === 'date') seed[f.key] = today;
    }
    return seed;
  })();

  const [answers, setAnswers] = useState(seededAnswers);
  const [photos, setPhotos] = useState(initialPhotos);
  const [saving, setSaving] = useState(false);

  const setA = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const handlePhoto = (k, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Foto 5MB\'den küçük olmalı'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotos((p) => ({ ...p, [k]: [...(p[k] || []), ev.target.result] }));
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (k, idx) => setPhotos((p) => ({
    ...p,
    [k]: (p[k] || []).filter((_, i) => i !== idx),
  }));

  // Şub 10, 2026: Başlangıç KM ile Bitiş KM doğrulaması.
  // Şablondaki alanların key/label değerlerinde "başlangıç/baslangic/start" ve
  // "bitiş/bitis/end" + "km" geçenleri tespit eder; Bitiş > Başlangıç olmak zorunda.
  const validateKm = () => {
    const norm = (s) => (s || '').toString().toLowerCase()
      .replaceAll('ı', 'i').replaceAll('ş', 's').replaceAll('ğ', 'g')
      .replaceAll('ü', 'u').replaceAll('ö', 'o').replaceAll('ç', 'c');
    const fields = a.form_template || [];
    const isKm = (f) => {
      const s = `${norm(f.key)} ${norm(f.label)}`;
      return s.includes('km');
    };
    const startField = fields.find((f) => {
      const s = `${norm(f.key)} ${norm(f.label)}`;
      return isKm(f) && (s.includes('baslangic') || s.includes('start') || s.includes('basla'));
    });
    const endField = fields.find((f) => {
      const s = `${norm(f.key)} ${norm(f.label)}`;
      return isKm(f) && (s.includes('bitis') || s.includes('end') || s.includes('son'));
    });
    if (!startField || !endField) return true;
    const sv = answers[startField.key];
    const ev = answers[endField.key];
    if (sv == null || sv === '' || ev == null || ev === '') return true;
    const sn = parseFloat(String(sv).replace(',', '.'));
    const en = parseFloat(String(ev).replace(',', '.'));
    if (Number.isNaN(sn) || Number.isNaN(en)) return true;
    if (en <= sn) {
      toast.error(`Bitiş KM (${en}), Başlangıç KM'den (${sn}) büyük olmalıdır`);
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validateKm()) return;
    setSaving(true);
    try {
      await api.post(`/planning/assignments/${a.id}/form-submission`, { answers, photos });
      toast.success('Form gönderildi');
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Form gönderilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`today-form-dialog-${a.id}`}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-bold">{a.route_code} formu</h2>
            <p className="text-xs text-slate-500">
              {new Date(a.assignment_date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
              {' · '}{a.depot_name}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" onPaste={(e) => { /* paste handler in AttachmentBox */ }}>
          {(a.form_template || []).map((f) => (
            <div key={f.key} className="space-y-1" data-testid={`today-form-field-${f.key}`}>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                {f.label}
                {f.required && <span className="text-rose-500">*</span>}
              </label>
              {f.help && <p className="text-[10px] text-slate-400">{f.help}</p>}
              {f.type === 'text' && (
                <Input value={answers[f.key] ?? ''} onChange={(e) => setA(f.key, e.target.value)} className="h-8 text-sm" />
              )}
              {f.type === 'number' && (
                <Input type="number" value={answers[f.key] ?? ''} onChange={(e) => setA(f.key, e.target.value)} className="h-8 text-sm" inputMode="decimal" />
              )}
              {f.type === 'time' && (
                <div className="flex items-center gap-1.5">
                  <Input
                    type="time"
                    value={answers[f.key] ?? ''}
                    onChange={(e) => setA(f.key, e.target.value)}
                    className="h-8 text-sm flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      const hh = String(d.getHours()).padStart(2, '0');
                      const mm = String(d.getMinutes()).padStart(2, '0');
                      setA(f.key, `${hh}:${mm}`);
                      // Diğer time alanları boşsa hatırlatma toast'u
                      const otherEmpty = (a.form_template || []).filter(
                        (g) => g.type === 'time' && g.key !== f.key && !(answers[g.key] && String(answers[g.key]).trim())
                      );
                      if (otherEmpty.length > 0) {
                        toast.warning(
                          `Hatırlatma: hâlâ boş saat alanı var → ${otherEmpty.map((g) => g.label).join(', ')}`,
                          { duration: 4000 }
                        );
                      }
                    }}
                    className="h-8 px-2 text-[11px] font-medium rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/60 dark:hover:bg-blue-900/50 whitespace-nowrap"
                    data-testid={`today-form-now-${f.key}`}
                    title="Şu anki saati ekle"
                  >
                    Şimdi
                  </button>
                </div>
              )}
              {f.type === 'date' && (
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={answers[f.key] ?? ''}
                    onChange={(e) => setA(f.key, e.target.value)}
                    className="h-8 text-sm flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      setA(f.key, `${y}-${m}-${day}`);
                    }}
                    className="h-8 px-2 text-[11px] font-medium rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/60 dark:hover:bg-blue-900/50 whitespace-nowrap"
                    data-testid={`today-form-today-${f.key}`}
                    title="Bugünü ekle"
                  >
                    Bugün
                  </button>
                </div>
              )}
              {f.type === 'boolean' && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={!!answers[f.key]} onCheckedChange={(v) => setA(f.key, !!v)} />
                  <span>Evet</span>
                </label>
              )}
              {f.type === 'select' && (
                <select
                  value={answers[f.key] ?? ''}
                  onChange={(e) => setA(f.key, e.target.value)}
                  className="block h-8 text-sm rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 px-2 w-full"
                >
                  <option value="">— Seçin —</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {f.type === 'photo' && (
                <div className="space-y-1.5">
                  <label className="inline-flex items-center gap-1 text-xs text-blue-600 cursor-pointer border border-blue-200 rounded px-2 py-1 hover:bg-blue-50">
                    <Camera className="h-3 w-3" /> Foto yükle
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhoto(f.key, e)} />
                  </label>
                  {(photos[f.key] || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-1.5">
                      {(photos[f.key] || []).map((src, i) => (
                        <div key={i} className="relative">
                          <img src={src} alt="" className="rounded border border-slate-200 w-full aspect-square object-cover" />
                          <button
                            onClick={() => removePhoto(f.key, i)}
                            className="absolute top-0.5 right-0.5 bg-rose-500 text-white rounded-full p-0.5"
                            type="button"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Genel ekler (Faz 5 — Şub 03 2026) */}
          <AttachmentBox assignmentId={a.id} />
        </div>
        <div className="flex justify-end gap-2 p-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>İptal</Button>
          <Button size="sm" onClick={submit} disabled={saving} data-testid={`today-form-submit-${a.id}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            {a.form_submitted ? 'Güncelle' : 'Gönder'}
          </Button>
        </div>
      </div>
    </div>
  );
}


/**
 * AttachmentBox — Form zorunlu alanlarının dışında ek dosya/foto yükleme.
 * --------------------------------------------------------------
 *  • Dosya seçer (multiple)
 *  • Kamera (mobil için capture)
 *  • Clipboard paste (Ctrl+V veya mobilde "yapıştır")
 *  • Her dosya bağımsız + paralel olarak upload edilir → kullanıcı beklemez
 *  • Yüklenen dosyalar liste halinde gösterilir, silinebilir
 *  • Form submit ile bağımsız: ek dosyalar zaten S3'e yazılmış durumda
 */
function AttachmentBox({ assignmentId }) {
  const [items, setItems] = useState([]);  // [{id?, file_name, size, status:'uploading'|'done'|'error', progress, error, tempId}]
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Mevcut ekleri yükle
  useEffect(() => {
    let cancelled = false;
    api.get(`/planning/assignments/${assignmentId}/attachments`)
      .then((r) => {
        if (!cancelled) {
          setItems((r.data?.data || []).map((d) => ({
            id: d.id, file_name: d.file_name, size: d.file_size,
            content_type: d.content_type, status: 'done', cdn_url: d.cdn_url,
          })));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assignmentId]);

  const uploadOne = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error(`${file.name}: 15MB üst sınırını aştı`);
      return;
    }
    const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
    const localPreview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setItems((prev) => [...prev, {
      tempId, file_name: file.name || 'foto.jpg', size: file.size,
      content_type: file.type, status: 'uploading', progress: 0, preview: localPreview,
    }]);

    const fd = new FormData();
    fd.append('file', file, file.name || 'foto.jpg');
    try {
      const res = await api.post(
        `/planning/assignments/${assignmentId}/attachments`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setItems((prev) => prev.map((it) => it.tempId === tempId ? { ...it, progress: pct } : it));
          },
        },
      );
      const d = res.data?.data;
      setItems((prev) => prev.map((it) => it.tempId === tempId ? {
        ...it, id: d.id, status: 'done', progress: 100, cdn_url: d.cdn_url,
      } : it));
    } catch (e) {
      setItems((prev) => prev.map((it) => it.tempId === tempId ? {
        ...it, status: 'error', error: e?.response?.data?.detail || 'Yüklenemedi',
      } : it));
      toast.error('Yükleme hatası: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadOne);  // paralel
    e.target.value = '';  // aynı dosya tekrar seçilebilsin
  };

  const onPaste = (e) => {
    const files = [];
    for (const item of e.clipboardData?.items || []) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      files.forEach(uploadOne);
      toast.info(`${files.length} dosya yapıştırıldı`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) files.forEach(uploadOne);
  };

  const removeItem = async (item) => {
    if (item.tempId && !item.id) {
      setItems((prev) => prev.filter((x) => x.tempId !== item.tempId));
      return;
    }
    try {
      await api.delete(`/planning/assignments/${assignmentId}/attachments/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      toast.error('Silinemedi: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div
      ref={containerRef}
      onPaste={onPaste}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      tabIndex={0}
      className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-3 space-y-2 focus:outline-none focus:border-blue-400 focus:bg-blue-50/30"
      data-testid="form-attachment-box"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
          <Paperclip className="h-3.5 w-3.5" /> Ek Dosyalar
          <span className="text-[10px] font-normal text-slate-400">(opsiyonel · foto, PDF, vb. — max 15MB)</span>
        </label>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs text-blue-600 border border-blue-200 rounded px-2 py-1 hover:bg-blue-50"
            data-testid="attach-file-btn"
          >
            <Upload className="h-3 w-3" /> Dosya
          </button>
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs text-emerald-600 border border-emerald-200 rounded px-2 py-1 hover:bg-emerald-50 md:hidden"
            data-testid="attach-camera-btn"
          >
            <Camera className="h-3 w-3" /> Çek
          </button>
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={onFilesPicked} className="hidden" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={onFilesPicked} className="hidden" />
      </div>

      <p className="text-[10px] text-slate-400 italic">
        💡 Bilgisayardan <kbd className="px-1 bg-slate-200 dark:bg-slate-700 rounded">Ctrl+V</kbd> ile ekran görüntüsü yapıştırabilirsiniz, dosyaları sürükle-bırak edebilirsiniz.
      </p>

      {loading && (
        <div className="text-xs text-slate-400 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Mevcut ekler yükleniyor…
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((it, i) => {
            const key = it.id || it.tempId || i;
            const isImg = (it.content_type || '').startsWith('image/') || it.preview;
            return (
              <div key={key} className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-1.5"
                   data-testid={`attach-item-${key}`}>
                <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {isImg && (it.preview || it.cdn_url) ? (
                    <img src={it.preview || it.cdn_url} alt="" className="w-full h-full object-cover" />
                  ) : isImg ? (
                    <ImageIcon className="h-4 w-4 text-slate-400" />
                  ) : (
                    <FileText className="h-4 w-4 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{it.file_name}</div>
                  <div className="text-[10px] text-slate-400 flex items-center gap-2">
                    {it.size != null && <span>{(it.size / 1024).toFixed(1)} KB</span>}
                    {it.status === 'uploading' && (
                      <span className="text-blue-500 flex items-center gap-1">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Yükleniyor… %{it.progress || 0}
                      </span>
                    )}
                    {it.status === 'done' && (
                      <span className="text-emerald-600 flex items-center gap-0.5">
                        <CheckCircle2 className="h-2.5 w-2.5" /> Yüklendi
                      </span>
                    )}
                    {it.status === 'error' && (
                      <span className="text-rose-600 truncate">{it.error}</span>
                    )}
                  </div>
                  {it.status === 'uploading' && (
                    <div className="w-full bg-slate-200 rounded-full h-0.5 mt-1">
                      <div className="bg-blue-500 h-0.5 rounded-full transition-all" style={{ width: `${it.progress || 0}%` }} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(it)}
                  className="p-1 rounded hover:bg-rose-50 text-rose-500"
                  title="Sil"
                  data-testid={`attach-remove-${key}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
